// @migration-in-progress
/**
 * Equipment Export Module - PDF generation for safety equipment inspection reports
 * 
 * Provides WBC3-compliant equipment inspection documentation including
 * LSA and FFE check reports with compliance tracking
 */

import { debug } from '../logging';
import {
  createPDF,
  getPageDimensions,
  setFont,
  checkPageBreak,
  addReportHeader,
  addReportFooter,
  addSectionHeading,
  addWrappedText,
  formatDate,
  formatDateTime,
  savePDF,
  handlePDFError,
  PDF_CONFIG
} from './pdfCore';

/**
 * Export equipment inspection reports as comprehensive PDF
 * @param {Array} equipmentChecks - Array of equipment check records
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportEquipmentReportPDF(equipmentChecks, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category = 'Unknown Category',
      vesselIMO = 'Not Specified'
    } = options;

    if (!equipmentChecks || equipmentChecks.length === 0) {
      alert('No equipment checks available to export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth } = getPageDimensions(pdf);
    
    let currentY = margin;

    // Add header
    currentY = addReportHeader(pdf, currentY, {
      title: 'Safety Equipment Inspection Report',
      vesselName,
      category
    });

    // Additional vessel info
    setFont(pdf, 'body');

    // Add Official Number (MCA requirement)
    if (options.vessel && options.vessel.officialNumber) {
      pdf.text(`Official Number: ${options.vessel.officialNumber}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    pdf.text(`IMO Number: ${vesselIMO}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight + 5;

    // WBC3 compliance notice
    setFont(pdf, 'small');
    pdf.setFont('helvetica', 'italic');
    pdf.text('This report complies with Workboat Code Edition 3 requirements for safety equipment inspections', margin, currentY);
    currentY += PDF_CONFIG.lineHeight + 5;

    // Generate summary statistics
    const lsaChecks = equipmentChecks.filter(check => check.type === 'lsa');
    const ffeChecks = equipmentChecks.filter(check => check.type === 'ffe');
    const passedChecks = equipmentChecks.filter(check => check.result === 'pass');
    const checksWithIssues = equipmentChecks.filter(check => check.result === 'issues');

    // Summary Section
    currentY = addSectionHeading(pdf, currentY, 'Summary', 'heading');

    setFont(pdf, 'body');
    pdf.text(`Total Equipment Checks: ${equipmentChecks.length}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`LSA Checks: ${lsaChecks.length}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`FFE Checks: ${ffeChecks.length}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`Checks Passed: ${passedChecks.length}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`Checks with Issues: ${checksWithIssues.length}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight + 5;

    // Equipment Check Records Section
    const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
      title: 'Safety Equipment Inspection Report (continued)',
      vesselName,
      category
    }), 30);
    currentY = pageBreak.currentY;

    currentY = addSectionHeading(pdf, currentY, 'Equipment Check Records', 'heading');

    // Sort checks by date (newest first)
    const sortedChecks = [...equipmentChecks].sort((a, b) => new Date(b.date) - new Date(a.date));

    sortedChecks.forEach((check, index) => {
      const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
        title: 'Safety Equipment Inspection Report (continued)',
        vesselName,
        category
      }), 25);
      currentY = pageBreak.currentY;

      // Check header
      setFont(pdf, 'subheading');
      const checkTitle = `${check.type === 'lsa' ? 'LSA' : 'FFE'} Check - ${formatDate(check.date)}`;
      pdf.text(checkTitle, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;

      // Result with color coding
      const resultColor = check.result === 'pass' ? [34, 197, 94] : [239, 68, 68];
      pdf.setFillColor(...resultColor);
      pdf.roundedRect(margin, currentY, 20, 6, 1, 1, 'F');
      pdf.setTextColor(255, 255, 255);
      setFont(pdf, 'small');
      pdf.text(check.result === 'pass' ? 'PASS' : 'ISSUES', margin + 10, currentY + 4, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      currentY += 10;

      // Inspector
      if (check.inspector) {
        setFont(pdf, 'body');
        pdf.text(`Inspector: ${check.inspector}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }

      // Items checked
      if (check.checkedItems && check.checkedItems.length > 0) {
        pdf.text('Items Checked:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        
        setFont(pdf, 'body');
        check.checkedItems.forEach(item => {
          pdf.text(`✓ ${item}`, margin + 10, currentY);
          currentY += PDF_CONFIG.lineHeight;
        });
        currentY += 3;
      }

      // Issues found
      if (check.issuesFound && check.issuesFound.length > 0) {
        setFont(pdf, 'subheading');
        pdf.text('Issues Found', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight + 3;

        setFont(pdf, 'body');
        check.issuesFound.forEach(issue => {
          currentY = addWrappedText(pdf, currentY, `• ${issue}`, { 
            fontType: 'body', 
            indent: 10,
            maxWidth: contentWidth - 15
          });
        });
        currentY += 3;
      }

      // Notes
      if (check.notes) {
        setFont(pdf, 'subheading');
        pdf.text('Notes', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight + 3;

        currentY = addWrappedText(pdf, currentY, check.notes, { 
          fontType: 'body', 
          indent: 10,
          maxWidth: contentWidth - 10
        });
        currentY += 5;
      }

      currentY += PDF_CONFIG.sectionSpacing / 2; // Space between checks
    });

    // Add footer
    addReportFooter(pdf, {
      customText: 'WBC3 Compliant Safety Equipment Inspection Documentation'
    });

    // Save the PDF
    const filename = savePDF(pdf, 'equipment_inspection_report', new Date());
    
    debug.log('Equipment inspection PDF export completed', { 
      filename, 
      checkCount: equipmentChecks.length,
      lsaChecks: lsaChecks.length,
      ffeChecks: ffeChecks.length
    });
    
    return true;
    
  } catch (error) {
    handlePDFError(error, 'equipment inspection report export');
    return false;
  }
}

/**
 * Export individual equipment check as detailed PDF report
 * @param {Object} equipmentCheck - Equipment check record
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportIndividualEquipmentCheckPDF(equipmentCheck, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category = 'Unknown Category',
      vesselIMO = 'Not Specified'
    } = options;

    if (!equipmentCheck) {
      alert('No equipment check data provided for export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth, pageWidth, pageHeight } = getPageDimensions(pdf);
    
    let currentY = margin;

    // Header with vessel information
    setFont(pdf, 'title');
    pdf.text('SAFETY EQUIPMENT INSPECTION', margin, currentY);
    currentY += 10;

    // Vessel details box
    setFont(pdf, 'body');
    pdf.setFillColor(248, 250, 252);
    pdf.rect(margin, currentY, contentWidth, 25, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.rect(margin, currentY, contentWidth, 25, 'S');
    
    currentY += 5;
    pdf.text(`Vessel: ${vesselName}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`Category: ${category}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`IMO Number: ${vesselIMO}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight + 10;

    // WBC3 Compliance Notice
    setFont(pdf, 'small');
    pdf.setFont('helvetica', 'italic');
    pdf.text('This inspection complies with Workboat Code Edition 3 safety equipment requirements', margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Inspection details
    setFont(pdf, 'subheading');
    pdf.text(`Inspection Type: ${equipmentCheck.type === 'lsa' ? 'Life-Saving Appliances (LSA)' : 'Fire Fighting Equipment (FFE)'}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight + 3;

    // Date and result
    setFont(pdf, 'body');
    pdf.text(`Date: ${formatDate(equipmentCheck.date)}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;

    if (equipmentCheck.inspector) {
      pdf.text(`Inspector: ${equipmentCheck.inspector}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    // Result with color coding
    const resultColor = equipmentCheck.result === 'pass' ? [34, 197, 94] : [239, 68, 68];
    pdf.setFillColor(...resultColor);
    pdf.roundedRect(margin, currentY, 30, 8, 2, 2, 'F');
    pdf.setTextColor(255, 255, 255);
    setFont(pdf, 'small');
    pdf.text(equipmentCheck.result === 'pass' ? 'PASS' : 'ISSUES', margin + 15, currentY + 5.5, { align: 'center' });
    pdf.setTextColor(0, 0, 0);
    currentY += 15;

    // Separator
    pdf.setLineWidth(0.5);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Items checked
    if (equipmentCheck.checkedItems && equipmentCheck.checkedItems.length > 0) {
      currentY = addSectionHeading(pdf, currentY, 'Items Inspected', 'heading');
      
      setFont(pdf, 'body');
      equipmentCheck.checkedItems.forEach(item => {
        pdf.text(`✓ ${item}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
      });
      currentY += 3;
    }

    // Issues found
    if (equipmentCheck.issuesFound && equipmentCheck.issuesFound.length > 0) {
      currentY = addSectionHeading(pdf, currentY, 'Issues Found', 'heading');

      setFont(pdf, 'body');
      equipmentCheck.issuesFound.forEach(issue => {
        currentY = addWrappedText(pdf, currentY, `• ${issue}`, { 
          fontType: 'body', 
          indent: 5,
          maxWidth: contentWidth - 10
        });
      });
      currentY += 3;
    }

    // Notes
    if (equipmentCheck.notes) {
      currentY = addSectionHeading(pdf, currentY, 'Notes', 'heading');
      
      currentY = addWrappedText(pdf, currentY, equipmentCheck.notes, { 
        fontType: 'body', 
        indent: 5,
        maxWidth: contentWidth - 5
      });
      currentY += 5;
    }

    // Footer
    let footerY = pageHeight - margin - 15;
    pdf.setLineWidth(0.5);
    pdf.line(margin, footerY, pageWidth - margin, footerY);
    footerY += 5;
    
    setFont(pdf, 'footer');
    const reportDate = new Date().toLocaleDateString('en-GB');
    const reportTime = new Date().toLocaleTimeString('en-GB', { hour12: false });
    pdf.text('Generated by FleetSkipper - WBC3 Compliant Maritime Software', margin, footerY);
    pdf.text(`Report generated: ${reportDate} ${reportTime}`, pageWidth - margin - 50, footerY);

    // Generate filename
    const dateStr = formatDate(equipmentCheck.date).replace(/\//g, '-');
    const checkType = equipmentCheck.type.toUpperCase();
    const filename = savePDF(pdf, `${checkType}_Check`, equipmentCheck.date, dateStr);
    
    debug.log('Individual equipment check PDF exported', { 
      filename, 
      checkType: equipmentCheck.type,
      checkDate: equipmentCheck.date
    });
    
    return true;
    
  } catch (error) {
    handlePDFError(error, 'individual equipment check export', 'Failed to generate equipment check report. Please try again.');
    return false;
  }
}